import { create } from "zustand";
import { writeAndCommit, deleteFile, commit as gitCommit } from "../lib/git-store";

// Document library — per-browser persistent collection of plain-text /
// markdown docs the user has imported. Channels reference these by
// id at attach time; the channel session itself copies the content
// into session.attached_documents so multiplayer teammates see the
// same context the AI sees. The library is the personal cache; the
// channel carries what it actually needs.
//
// Storage: localStorage. Caps at LIBRARY_BYTES_CAP total — beyond
// that the user has to remove docs to add new ones. No per-doc cap
// in the library itself; the per-channel attach cap enforces the
// "don't bloat synced channels" rule (see sessionStore.attachDocument).

const STORAGE_KEY = "playground.library_documents";

/** Total bytes across all library docs the store will accept. Past
 *  this, addDocument throws; UI surfaces the error so the user knows
 *  to prune. 5MB rounds nicely under localStorage's typical 5-10MB
 *  origin cap, leaving room for channels, keys, etc. */
export const LIBRARY_BYTES_CAP = 5 * 1024 * 1024;

export interface LibraryDocument {
  id: string;
  name: string;
  content: string;
  bytes: number;
  added_at: string;
  /** Free-form one-word labels for organizing — "spec", "rfc",
   *  "transcript", "q3". Used by the search filter and by humans
   *  scanning the sidebar. No fixed vocabulary; users pick what
   *  works for them. */
  tags?: string[];
}

type DocumentState = {
  documents: LibraryDocument[];
  /** Returns the new doc's id, or throws if the cap would be exceeded. */
  addDocument: (name: string, content: string) => string;
  removeDocument: (id: string) => void;
  renameDocument: (id: string, name: string) => void;
  setTags: (id: string, tags: string[]) => void;
};

function load(): LibraryDocument[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LibraryDocument[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[library] failed to load:", e);
    return [];
  }
}

function persist(docs: LibraryDocument[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(docs));
  } catch (e) {
    console.warn("[library] persist failed:", e);
  }
}

function totalBytes(docs: LibraryDocument[]): number {
  return docs.reduce((sum, d) => sum + d.bytes, 0);
}

// Git-canonical write-through. Library docs land at
// data/library/<id>.json; fire-and-forget so the UI isn't blocked
// on the in-browser git layer; failures log but don't throw
// (localStorage stays authoritative for reads during Phase 2).
function shortId(id: string): string {
  return id.slice(0, 8);
}

function gitWriteDoc(doc: LibraryDocument, message: string): void {
  const path = `data/library/${doc.id}.json`;
  const body = JSON.stringify(doc, null, 2);
  void writeAndCommit(path, body, message).catch((e) => {
    console.warn("[git-store] library write failed:", doc.id, e);
  });
}

function gitDeleteDoc(docId: string, name: string): void {
  const path = `data/library/${docId}.json`;
  void (async () => {
    try {
      const removed = await deleteFile(path);
      if (removed) await gitCommit(`delete document ${shortId(docId)} (${name})`);
    } catch (e) {
      console.warn("[git-store] library delete failed:", docId, e);
    }
  })();
}

export const useDocumentStore = create<DocumentState>((set, get) => ({
  documents: load(),

  addDocument: (name, content) => {
    const trimmedName = name.trim() || "untitled";
    const bytes = new TextEncoder().encode(content).length;
    const existing = get().documents;
    if (totalBytes(existing) + bytes > LIBRARY_BYTES_CAP) {
      throw new Error(
        `Library is full (${(LIBRARY_BYTES_CAP / 1024 / 1024).toFixed(0)} MB cap). ` +
        `Remove a doc to make room.`,
      );
    }
    const doc: LibraryDocument = {
      id: crypto.randomUUID(),
      name: trimmedName,
      content,
      bytes,
      added_at: new Date().toISOString(),
    };
    const next = [doc, ...existing];
    persist(next);
    set({ documents: next });
    gitWriteDoc(doc, `create document ${shortId(doc.id)} (${doc.name}, ${(doc.bytes / 1024).toFixed(1)}KB)`);
    return doc.id;
  },

  removeDocument: (id) => {
    const prev = get().documents.find((d) => d.id === id);
    const next = get().documents.filter((d) => d.id !== id);
    persist(next);
    set({ documents: next });
    if (prev) gitDeleteDoc(id, prev.name);
  },

  renameDocument: (id, name) => {
    const trimmed = name.trim() || "untitled";
    const next = get().documents.map((d) =>
      d.id === id ? { ...d, name: trimmed } : d,
    );
    persist(next);
    set({ documents: next });
    const updated = next.find((d) => d.id === id);
    if (updated) gitWriteDoc(updated, `update document ${shortId(id)} (rename → ${trimmed})`);
  },

  setTags: (id, tags) => {
    // Normalize: trim each, drop empties, lowercase, dedupe.
    const norm = Array.from(new Set(
      tags.map((t) => t.trim().toLowerCase()).filter((t) => t.length > 0),
    ));
    const next = get().documents.map((d) =>
      d.id === id ? { ...d, tags: norm.length > 0 ? norm : undefined } : d,
    );
    persist(next);
    set({ documents: next });
    const updated = next.find((d) => d.id === id);
    if (updated) {
      const tagList = norm.length > 0 ? norm.join(", ") : "(none)";
      gitWriteDoc(updated, `update document ${shortId(id)} (tags: ${tagList})`);
    }
  },
}));
